import type { QuoteResponse } from '@noconflict/contracts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormat } from '../../../shared/i18n/format';
import { cn } from '../../../shared/lib/cn';

export type SummaryRow = {
  label: string;
  /** Null shows "Not chosen yet". */
  value: string | null;
  /** Left out of the bottom bar on a phone, where there is room for the choices and the price only. */
  wideOnly?: boolean;
};

/** The live quote as the summary shows it. */
export type SummaryPrice =
  /** Nothing to quote yet: a choice that sets the price is still open. */
  | { state: 'incomplete' }
  /** No price line at all: the organization hides amounts, or that is not known yet. */
  | { state: 'hidden' }
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'ready'; quote: QuoteResponse };

/**
 * What is being ordered and what it costs, beside the form: a side panel on a wide screen, a bar fixed to the
 * bottom on a phone. The price is the net amount from the worker's quote, the only amount a customer is told; an
 * organization whose amounts are hidden (or not known yet) gets no price line at all. `action` is the submit button.
 */
export function PriceSummary({
  rows,
  price,
  missing,
  action,
  className,
}: {
  rows: SummaryRow[];
  price: SummaryPrice;
  /** What is still needed before the order can be submitted, as phrases ("choose a scope"). */
  missing: string[];
  action: ReactNode;
  className?: string;
}) {
  const { t, i18n } = useTranslation('order');
  const { formatCurrency } = useFormat();
  // A `hidden` quote answer hides the line too, so a stale or racing answer can never show one.
  const hidden = price.state === 'hidden' || (price.state === 'ready' && 'hidden' in price.quote);
  const list = new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' });

  return (
    <aside
      aria-label={t('review.title')}
      className={cn(
        'fixed inset-x-0 bottom-0 z-20 grid gap-3 border-t bg-card p-4 shadow-overlay',
        'lg:sticky lg:inset-x-auto lg:top-6 lg:bottom-auto lg:z-auto lg:gap-4 lg:self-start lg:rounded-lg lg:border lg:p-6 lg:shadow-card',
        className,
      )}
    >
      <h2 className="sr-only font-bold text-heading lg:not-sr-only lg:text-lg">
        {t('review.title')}
      </h2>
      <dl className="flex flex-wrap gap-x-4 gap-y-1 text-sm lg:grid lg:gap-3 lg:text-base">
        {rows.map((row) => (
          <div key={row.label} className={cn(row.wideOnly && 'hidden lg:block')}>
            <dt className="sr-only text-sm text-muted-foreground lg:not-sr-only">{row.label}</dt>
            <dd className={cn('break-words', row.value === null && 'text-muted-foreground')}>
              {row.value ?? t('review.notChosen')}
            </dd>
          </div>
        ))}
      </dl>
      {hidden ? null : (
        // Hidden amounts (an org on account whose billing info staff have hidden) get no price line at all.
        <p aria-live="polite" className="lg:border-t lg:pt-4">
          {price.state === 'incomplete' ? (
            <span className="text-sm text-muted-foreground">{t('review.priceNeedsScope')}</span>
          ) : (
            <>
              <span className="text-sm text-muted-foreground">{t('review.price')}: </span>
              <span className="font-bold text-heading lg:block lg:text-2xl">
                {price.state === 'loading'
                  ? t('review.priceLoading')
                  : price.state === 'error'
                    ? t('review.priceError')
                    : 'amountCents' in price.quote
                      ? formatCurrency(price.quote.amountCents, price.quote.currency)
                      : null}
              </span>
            </>
          )}
        </p>
      )}
      {missing.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('review.missingIntro')} {list.format(missing)}.
        </p>
      ) : null}
      <div>{action}</div>
    </aside>
  );
}
